<template>
  <PageHeader :showBreadcrumb="true" />
  <form v-if="authStore.isAdminUser && appStore.networks.length > 0" @submit.stop.prevent="onSubmit">
    <div class="container">
      <DataTable v-model:editingRows="editingRows" :value="appStore.getNetworksAwaitingReview()"
                 editMode="row" dataKey="id" @row-edit-save="onRowEditSave"
                 stripedRows showGridlines paginator :rows="25"
                 :pt="{
                   table: { style: 'min-width: 50rem' },
                   column: {
                     bodycell: ({ state }) => ({
                       style:  state['d_editing']&&'padding-top: 0.75rem; padding-bottom: 0.75rem'
                     })
                   }
                 }">
        <template #empty> No Data. </template>
        <Column field="carrier" header="Entity" :sortable="true"/>
        <Column field="networkName" header="Network Name" :sortable="true">
          <template #editor="{ data, field }">
            <InputText v-model="data[field]" fluid />
          </template>
        </Column>
        <Column field="networkId" header="Network ID" :sortable="true">
          <template #editor="{ data, field }">
            <InputText v-model="data[field]" fluid />
          </template>
        </Column>
        <Column field="marketType" header="Market Type" :sortable="true">
          <template #editor="{ data, field }">
            <Select v-model="data[field]" :options="marketTypes" optionLabel="label" optionValue="value" placeholder="Select Market Type" fluid>
              <template #option="slotProps">
                <Tag :value="slotProps.option.value" :severity="getSeverity(slotProps.option.value)"/>
              </template>
            </Select>
          </template>
          <template #body="slotProps">
            <Tag :value="slotProps.data.marketType" :severity="getSeverity(slotProps.data.marketType)"/>
          </template>
        </Column>
        <Column field="status" header="Network Status" :sortable="true">
          <template #editor="{ data, field }">
            <Select v-model="data[field]" :options="statuses" optionLabel="label" optionValue="value" placeholder="Select Status" fluid>
              <template #option="slotProps">
                <Tag :value="slotProps.option.value" :severity="getSeverity(slotProps.option.value)" />
              </template>
            </Select>
          </template>
          <template #body="slotProps">
            <Tag :value="slotProps.data.status" :severity="getSeverity(slotProps.data.status)" />
          </template>
        </Column>
        <Column header="Actions" :rowEditor="true" style="width: 10%; min-width: 8rem" bodyStyle="text-align: center"/>
      </DataTable>
    </div>
  </form>
</template>

<script setup>
import {ref} from 'vue';
import PageHeader from "@/components/common/PageHeader.vue";
import {useAppStore} from "@/stores/appStore.js";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import {NETWORK_MARKET_TYPE, NETWORK_STATUSES, TOAST_SAVE_LIFE} from "@/utils/constants.js";
import {updateNetwork} from '@/services/networkService.js';
import {NetworkModel} from "@/models/NetworkModel.js";
import {useToast} from "primevue/usetoast";
import {useAuthStore} from "@/stores/authStore.js";

const appStore = useAppStore();
const authStore = useAuthStore();
const toast = useToast();

const editingRows = ref([]);

const statuses = ref(
    Object.entries(NETWORK_STATUSES).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

const getSeverity = (value) => {
  // Handle status values
  switch (value) {
    case NETWORK_STATUSES.Reviewing:
      return 'warn';

    case NETWORK_STATUSES.Inactive:
      return 'danger';

    case NETWORK_STATUSES.Active:
      return 'success';

    // Handle market type values
    case 'PBM':
      return 'info';

    default:
      return null;
  }
};

const marketTypes = ref(
    Object.entries(NETWORK_MARKET_TYPE).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

const onRowEditSave = async (event) => {
  const {newData, index} = event;
  const networkModel = new NetworkModel(newData);
  try {
    await updateNetwork(networkModel);
    await appStore.fetchAllNetworks();
    toast.add({ group: 'top-toast', severity: 'success', summary: 'Successfully Updated Network', detail: `The ${networkModel.networkName} (${networkModel.networkId}) network has been updated.`, life: TOAST_SAVE_LIFE });
  } catch (e) {
    toast.add({ group: 'top-toast', severity: 'error', summary: 'Error Updating Network', detail: `The ${networkModel.networkName} (${networkModel.networkId}) network could not be updated.\n${e}`, life: TOAST_SAVE_LIFE });
  }
};
</script>

<style scoped>
.tx-p {
  padding: 1.5rem;
  height: 200px;
}
</style>